import { useTranslate } from "@tolgee/react";
import { useHyperAccount } from "#/hooks/use-hyperliquid-account";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { formatUsd, toNumber } from "#/lib/formatters";

export function AccountOverview() {
	const { t } = useTranslate();
	const { accountState } = useHyperAccount();
	const { isUnified, isDexAbstraction, spotUsdc, spotUsdcHold } =
		useUnifiedBalance();

	// Card renders unconditionally to reserve its final height from first paint —
	// it sits above the chart + all tables, so a null→card pop-in was the dominant
	// portfolio CLS source. Loading (accountState undefined) → "—"; an empty
	// account resolves to a zero-object → accurate "$0.00".
	const perpAccountValue = toNumber(
		accountState?.marginSummary?.accountValue ??
			accountState?.crossMarginSummary.accountValue,
	);
	// See ACCOUNT.md → Balance display rules. Equity = positions MTM + free
	// spot. spot.hold is already inside perpAccountValue for unified, so
	// subtracting it yields the free spot portion. dexAbstraction uses the
	// same shape (main spot.hold typically 0, so subtraction is a no-op).
	const equity =
		isUnified || isDexAbstraction
			? perpAccountValue + spotUsdc - spotUsdcHold
			: perpAccountValue;
	const margin =
		accountState?.marginSummary ?? accountState?.crossMarginSummary;
	const marginUsed = toNumber(margin?.totalMarginUsed);
	const notionalPos = Math.abs(toNumber(margin?.totalNtlPos));

	const availableBalance = Math.max(0, equity - marginUsed);
	const leverage = equity > 0 ? notionalPos / equity : 0;
	const marginFraction = equity > 0 ? (marginUsed / equity) * 100 : 0;

	// Placeholder until account data loads — keeps the 5-stat grid height stable.
	const orDash = (value: string) => (accountState ? value : "—");

	return (
		<div>
			<h2 className="mb-4 text-body2-bold text-foreground">
				{t("portfolio.account-overview")}
			</h2>
			{/* repeat(5,1fr): 내용이 칸보다 길면 잘리는 대신 트랙이 늘어남 — wallet-overview와 동일 */}
			<div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-[repeat(5,1fr)] sm:gap-x-0 sm:divide-x sm:divide-border">
				<Stat
					label={t("portfolio.total-equity")}
					value={orDash(formatUsd(equity))}
				/>
				<Stat
					label={t("portfolio.free-collateral")}
					value={orDash(formatUsd(availableBalance))}
				/>
				<Stat
					label={t("portfolio.leverage")}
					value={orDash(`${leverage.toFixed(2)}x`)}
				/>
				<Stat
					label={t("portfolio.used-collateral")}
					value={orDash(formatUsd(marginUsed))}
				/>
				<Stat
					label={t("portfolio.margin-fraction")}
					value={orDash(`${marginFraction.toFixed(1)}%`)}
				/>
			</div>
		</div>
	);
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<div className="sm:pl-6 sm:pr-6 sm:first:pl-0 sm:last:pr-0">
			<p className="text-label2 text-muted-foreground">{label}</p>
			<p className="text-title2 text-foreground">{value}</p>
		</div>
	);
}
